import React, {
  useCallback,
  useEffect,
  useState,
  memo,
  useRef,
  useMemo,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowDownTrayIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import SaveNoteButton from "../notes/SaveNoteButton";
import logo from "../assets/logo.svg";
import { useAuthContext } from "../contexts/AuthContext";
import styles from "./ThreadContent.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import {
  getToolActivityDisplay,
  type ToolActivityStatus,
} from "../utils/toolActivity";
import { formatChatCopyText } from "../utils/chatCopy";
import { formatReferenceExcerpt } from "../utils/referenceExcerpt";
import { articleReferenceUrl, type ArticleDetails } from "../utils/articleReference";
import { exportTableAsPdf } from "../utils/tablePdfExport";
import {
  DISLIKE_REASONS,
  type ChatReaction,
  type ChatReactionValue,
  type DislikeReasonCode,
} from "../types/chatFeedback";

type ReactionChangeHandler = (
  messageId: string,
  reaction: ChatReactionValue | null,
  reasonCodes: DislikeReasonCode[]
) => Promise<boolean>;

function AuthenticatedSaveNote(props: { messageId: string; savedNoteId?: string | null }) {
  const { user } = useAuthContext();
  return user ? <SaveNoteButton {...props} /> : null;
}

// Define the structure for a message in a thread. Content type can be text or JSON
interface MessageProps {
  message_id?: string;
  content: string | any;
  sender: "user" | "ai";
  timestamp: string;
  // If true, this AI message represents a tool-call bubble and should be rendered
  // without the AI logo header and without the timestamp footer.
  isToolCall?: boolean;
  isAgentCommentary?: boolean;
  commentaryId?: string;
  isCommentaryStreaming?: boolean;
  generationId?: string;
  showAssistantLogo?: boolean;
  processing_status?: "pending" | "processing" | "complete" | "error";
  error_message?: string;
  progressSteps?: Array<{
    id: string;
    label: string;
    kind?: "status" | "commentary";
  }>;
  // Structured fields from Agent3 responses
  answer?: string;
  justification?: string;
  references?: Array<{
    citedText: string;
    reference: string;
    pageNumber: string;
    type?: string;
    details?: ArticleDetails & {
      page_number?: number;
      edition?: string | number;
      authors?: string[];
      journal?: string;
      date?: string;
      doi?: string;
    };
  }>;
  researchItems?: MessageProps[];
  researchIsActive?: boolean;
  researchIsOpen?: boolean;
  onResearchToggle?: () => void;
  savedNoteId?: string | null;
  userReaction?: ChatReaction | null;
  reactionPending?: boolean;
  reactionError?: string;
  onReactionChange?: ReactionChangeHandler;
}

// Props for ThreadContent component
interface ThreadContentProps {
  threadId?: string;
  threadMessages?: Array<{
    message_id: string;
    content: string | any;
    sender: "user" | "ai";
    timestamp: string;
    isToolCall?: boolean;
    isAgentCommentary?: boolean;
    commentaryId?: string;
    isCommentaryStreaming?: boolean;
    generationId?: string;
    processing_status?: "pending" | "processing" | "complete" | "error";
    error_message?: string;
    progressSteps?: Array<{
      id: string;
      label: string;
      kind?: "status" | "commentary";
    }>;
    answer?: string;
    justification?: string;
    references?: Array<{
      citedText: string;
      reference: string;
      pageNumber: string;
      type?: string;
      details?: any;
    }>;
    savedNoteId?: string | null;
  userReaction?: ChatReaction | null;
  }>;
  isLoading?: boolean;
  processingStatus?: string;
  isTransitioning?: boolean;
  // When this counter increments, force a smooth scroll to bottom (e.g., on user send)
  scrollToBottomRequest?: number;
  reactionPendingMessageIds?: Set<string>;
  reactionErrors?: Record<string, string>;
  onReactionChange?: ReactionChangeHandler;
}

interface ResearchStep {
  id: string;
  label: string;
  source: string;
  status: ToolActivityStatus;
}

const normalizeResearchStep = (message: MessageProps): ResearchStep => {
  const fallbackId = message.message_id || `${message.timestamp}-${Math.random()}`;
  const display = getToolActivityDisplay(message.content);
  return {
    id: fallbackId,
    label: display.label,
    source: display.source,
    status: display.status,
  };
};

interface ResearchTimelineProps {
  items: MessageProps[];
  isActive: boolean;
  isOpen: boolean;
  onToggle: () => void;
  variant?: "standalone" | "embedded";
}

const ResearchTimeline: React.FC<ResearchTimelineProps> = memo(
  ({ items, isActive, isOpen, onToggle, variant = "standalone" }) => {
    const steps = items.map(normalizeResearchStep);
    const completedCount = steps.filter(
      (step) => step.status !== "running"
    ).length;
    const hasRunningStep = steps.some((step) => step.status === "running");
    const summary = hasRunningStep
      ? `${completedCount}/${steps.length} complete`
      : `${steps.length} ${steps.length === 1 ? "search" : "searches"} complete`;

    return (
      <div
        className={`${styles.researchTimeline} ${variant === "embedded" ? styles.researchTimelineEmbedded : ""
          } ${isActive ? styles.researchTimelineActive : ""
          }`}
      >
        <button
          type="button"
          className={styles.researchTimelineHeader}
          onClick={onToggle}
          aria-expanded={isOpen}
        >
          <span className={styles.researchHeaderLeft}>
            <span className={styles.researchHeaderIcon} aria-hidden="true">
              <MagnifyingGlassIcon />
            </span>
            <span className={styles.researchHeaderText}>
              <span className={styles.researchTitle}>
                {hasRunningStep ? "Searching sources" : "Research"}
              </span>
              <span className={styles.researchSubtitle}>{summary}</span>
            </span>
          </span>
          <ChevronRightIcon
            className={`${styles.researchChevron} ${isOpen ? styles.researchChevronOpen : ""
              }`}
          />
        </button>

        <div
          className={`${styles.researchTimelineBody} ${isOpen
            ? styles.researchTimelineBodyOpen
            : styles.researchTimelineBodyClosed
            }`}
          aria-hidden={!isOpen}
        >
          {steps.map((step) => (
            <div key={step.id} className={styles.researchStep}>
              <span
                key={`${step.id}-${step.status}`}
                className={`${styles.researchStepStatus} ${
                  step.status === "running"
                    ? styles.researchStepStatusRunning
                    : step.status === "error"
                      ? styles.researchStepStatusError
                      : styles.researchStepStatusComplete
                }`}
                aria-hidden="true"
              >
                {step.status === "complete" && (
                  <svg viewBox="0 0 12 12" fill="none">
                    <path
                      d="m3 6.2 1.8 1.8L9 3.8"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {step.status === "error" && "!"}
              </span>
              <span className={styles.researchStepLabel}>{step.label}</span>
              <span className={styles.researchStepSource}>{step.source}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

// Animated dots component for thinking state (. -> .. -> ... -> repeat)
const ThinkingDots: React.FC = () => {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev >= 3 ? 1 : prev + 1));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className={styles.thinkingDots}>
      {'.'.repeat(dotCount)}
    </span>
  );
};

interface MarkdownTableProps
  extends React.TableHTMLAttributes<HTMLTableElement> {
  node?: unknown;
}

export const MarkdownTable: React.FC<MarkdownTableProps> = (props) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const [exportStatus, setExportStatus] = useState<
    "idle" | "exporting" | "failed"
  >("idle");
  const tableProps = { ...props };
  delete tableProps.node;

  const handleExport = async () => {
    if (!tableRef.current || exportStatus === "exporting") return;

    setExportStatus("exporting");
    try {
      await exportTableAsPdf(tableRef.current, logo);
      setExportStatus("idle");
    } catch (error) {
      console.error("Failed to export table PDF:", error);
      setExportStatus("failed");
      window.setTimeout(() => setExportStatus("idle"), 2500);
    }
  };

  return (
    <div className={styles.markdownTableBlock}>
      <div className={styles.markdownTableWrapper}>
        <table {...tableProps} ref={tableRef} />
      </div>
      <div className={styles.markdownTableFooter}>
        <button
          type="button"
          className={styles.markdownTableExportButton}
          onClick={handleExport}
          disabled={exportStatus === "exporting"}
          title="Download this table as a branded A4 PDF"
          aria-label={
            exportStatus === "exporting"
              ? "Exporting table as PDF"
              : exportStatus === "failed"
                ? "Retry table PDF export"
                : "Export table as PDF"
          }
        >
          {exportStatus === "exporting" ? (
            <span className={styles.markdownTableExportSpinner} aria-hidden="true" />
          ) : (
            <ArrowDownTrayIcon aria-hidden="true" />
          )}
          <span>
            {exportStatus === "exporting"
              ? "Preparing PDF"
              : exportStatus === "failed"
                ? "PDF failed"
                : "Export PDF"}
          </span>
        </button>
      </div>
    </div>
  );
};

const markdownComponents = {
  table: MarkdownTable,
  a: ({ node, children, ...props }: React.ComponentPropsWithoutRef<"a"> & { node?: unknown }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
};

const ChatMarkdown: React.FC<{ children: string }> = ({ children }) => (
  <div className={styles.markdownContent}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={markdownComponents}
    >
      {children}
    </ReactMarkdown>
  </div>
);

interface ActivityProgressProps {
  steps: Array<{
    id: string;
    label: string;
    kind?: "status" | "commentary";
  }>;
}

const ActivityProgress: React.FC<ActivityProgressProps> = memo(
  ({ steps }) => {
    const latestStatus = [...steps]
      .reverse()
      .find((step) => step.kind !== "commentary");
    const visibleSteps = latestStatus ? [latestStatus] : [];
    const latestStep = visibleSteps[visibleSteps.length - 1];

    if (visibleSteps.length === 0) return null;

    return (
      <div className={styles.activityProgress} aria-live="polite">
        <span className={styles.activityProgressSpinner} aria-hidden="true" />
        <div className={styles.activityProgressContent}>
          <span className={styles.activityProgressTitle}>Working through it</span>
          <div className={styles.activityProgressViewport}>
            <div
              key={latestStep?.id}
              className={styles.activityProgressLines}
            >
              {visibleSteps.map((step, index) => (
                <span
                  key={step.id}
                  className={`${styles.activityProgressLine} ${
                    step.kind === "commentary"
                      ? styles.activityProgressLineCommentary
                      : styles.activityProgressLineStatus
                  } ${
                    index < visibleSteps.length - 1
                      ? styles.activityProgressLinePrevious
                      : styles.activityProgressLineCurrent
                  }`}
                >
                  {step.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

// Message component to display individual messages
const Message: React.FC<MessageProps> = memo(
  ({
    message_id,
    content,
    sender,
    timestamp,
    answer,
    justification,
    references,
    isToolCall,
    isAgentCommentary,
    isCommentaryStreaming,
    showAssistantLogo,
    processing_status,
    error_message,
    progressSteps,
    researchItems,
    researchIsActive = false,
    researchIsOpen = false,
    onResearchToggle,
    userReaction,
    savedNoteId,
    reactionPending = false,
    reactionError,
    onReactionChange,
  }) => {
    // Determine styling based on sender (user or AI)
    const isUser = sender === "user";

    // Format timestamp for display
    const formatTimestamp = (timestamp: string) => {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    // Check if this is a structured AI response
    const hasStructuredResponse =
      !isUser &&
      (answer !== undefined ||
        justification !== undefined ||
        references !== undefined);

    // Copy functionality for AI messages
    const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">("idle");

    const handleCopy = async () => {
      const copyText = formatChatCopyText({
        answer,
        justification,
        references,
        content,
      });

      const copyWithSelectionFallback = () => {
        const textArea = document.createElement("textarea");
        textArea.value = copyText;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, textArea.value.length);

        try {
          return document.execCommand("copy");
        } finally {
          document.body.removeChild(textArea);
        }
      };

      try {
        const copiedFallback = copyWithSelectionFallback();
        if (!copiedFallback) {
          if (!navigator.clipboard?.writeText) {
            throw new Error("Clipboard API unavailable");
          }
          await navigator.clipboard.writeText(copyText);
        }
        setCopyStatus("copied");
        setTimeout(() => setCopyStatus("idle"), 2000);
      } catch (err) {
        setCopyStatus("failed");
        setTimeout(() => setCopyStatus("idle"), 2000);
        console.error("Failed to copy:", err);
      }
    };

    const [reasonsOpen, setReasonsOpen] = useState(false);

    useEffect(() => {
      if (userReaction?.reaction !== "dislike") {
        setReasonsOpen(false);
      }
    }, [userReaction?.reaction]);

    const handleReactionClick = (nextReaction: ChatReactionValue) => {
      if (!message_id || !onReactionChange || reactionPending) return;
      if (userReaction?.reaction === nextReaction) {
        setReasonsOpen(false);
        void onReactionChange(message_id, null, []);
        return;
      }

      if (nextReaction === "dislike") {
        setReasonsOpen(true);
        void onReactionChange(message_id, "dislike", []);
      } else {
        setReasonsOpen(false);
        void onReactionChange(message_id, "like", []);
      }
    };

    const toggleReason = (reason: DislikeReasonCode) => {
      if (!message_id || !onReactionChange || reactionPending) return;
      const selected = userReaction?.reason_codes || [];
      const reasonCodes = selected.includes(reason)
        ? selected.filter((code) => code !== reason)
        : DISLIKE_REASONS.map((item) => item.code).filter(
            (code) => code === reason || selected.includes(code)
          );
      void onReactionChange(message_id, "dislike", reasonCodes);
    };

    // State for references dropdown
    const [refsOpen, setRefsOpen] = useState(false);
    const toggleRefs = () => setRefsOpen(!refsOpen);
    const hasActiveProcessingStatus =
      processing_status === "pending" || processing_status === "processing";
    const hasActivityProgress =
      !isUser &&
      !isToolCall &&
      hasActiveProcessingStatus &&
      Array.isArray(progressSteps) &&
      progressSteps.length > 0;
    const hasResearchActivity =
      !isUser &&
      !isToolCall &&
      Array.isArray(researchItems) &&
      researchItems.length > 0 &&
      Boolean(onResearchToggle);
    const hasFallbackText =
      typeof content === "string"
        ? content.trim().length > 0
        : Boolean(content);
    const canShowAnswerActions =
      !isUser &&
      !isToolCall &&
      !isAgentCommentary &&
      processing_status !== "error" &&
      !error_message &&
      !hasActiveProcessingStatus &&
      Boolean(
        answer ||
          justification ||
          (references && references.length > 0) ||
          hasFallbackText
      );
    const showsOnlyProcessingIndicator =
      !isUser &&
      hasActiveProcessingStatus &&
      !hasActivityProgress &&
      !hasFallbackText &&
      !hasStructuredResponse &&
      !hasResearchActivity;
    const showsOnlyActivityProgress =
      hasActivityProgress &&
      !hasFallbackText &&
      !hasStructuredResponse &&
      !hasResearchActivity;
    const hasVisibleContent =
      isUser ||
      isToolCall ||
      hasActiveProcessingStatus ||
      processing_status === "error" ||
      hasStructuredResponse ||
      hasFallbackText ||
      hasActivityProgress ||
      hasResearchActivity;
    const hidesAssistantLogo =
      !isUser &&
      (showAssistantLogo === false ||
        (showAssistantLogo !== true &&
          (Boolean(isToolCall) ||
            Boolean(isAgentCommentary) ||
            showsOnlyProcessingIndicator ||
            showsOnlyActivityProgress ||
            (hasResearchActivity &&
              !hasFallbackText &&
              !hasStructuredResponse))));
    const hidesAssistantTimestamp =
      !isUser &&
      (Boolean(isToolCall) ||
        Boolean(isAgentCommentary) ||
        showsOnlyProcessingIndicator ||
        showsOnlyActivityProgress ||
        (hasResearchActivity &&
          !hasFallbackText &&
          !hasStructuredResponse));

    if (!hasVisibleContent) {
      return null;
    }

    return (
      <div
        className={`${styles.messageContainer} ${isUser ? styles.messageContainerUser : styles.messageContainerAi
          }`}
        data-message-kind={
          isUser
            ? "user"
            : isToolCall
              ? "tool-call"
              : isAgentCommentary
                ? "agent-commentary"
                : "assistant"
        }
      >
        {/* Brand the whole investigation once, above its first commentary. */}
        {!hidesAssistantLogo && (
          <div
            className={`${styles.messageSender} ${isUser ? styles.messageSenderUser : styles.messageSenderAi
              }`}
          >
            {isUser ? (
              "You"
            ) : (
              <img src={logo} alt="logo" className={styles.messageLogo} />
            )}
          </div>
        )}

        {/* For AI messages, use wrapper for copy button. For user messages, render bubble directly */}
        {isUser ? (
          /* User message bubble - no wrapper needed */
          <div
            className={`${styles.messageBubble} ${styles.messageBubbleUser}`}
          >
            <div className={styles.messageFallbackContent}>
              {typeof content === "string" ? content : JSON.stringify(content)}
            </div>
          </div>
        ) : (
          /* AI message bubble wrapper with copy button */
          <div className={styles.messageBubbleWrapperAi}>
            <div
              className={`${styles.messageBubble} ${styles.messageBubbleAi} ${hasStructuredResponse
                ? styles.messageBubbleAiStructured
                : isAgentCommentary
                  ? styles.messageBubbleAiCommentary
                  : styles.messageBubbleAiRegular
                } ${showsOnlyProcessingIndicator ? styles.messageBubbleAiProcessing : ""} ${showsOnlyActivityProgress ? styles.messageBubbleAiActivityOnly : ""} ${isCommentaryStreaming ? styles.messageBubbleAiCommentaryStreaming : ""}`}
            >
              {hasActivityProgress && !hasResearchActivity && (
                <ActivityProgress
                  steps={progressSteps as ActivityProgressProps["steps"]}
                />
              )}

              {hasResearchActivity && (
                <ResearchTimeline
                  items={researchItems as MessageProps[]}
                  isActive={researchIsActive}
                  isOpen={researchIsOpen}
                  onToggle={onResearchToggle as () => void}
                  variant="embedded"
                />
              )}

              {hasStructuredResponse ? (
                // Render structured AI response in a single column
                <div className={styles.messageStructuredContent}>
                  {/* Preliminary text (if any) */}
                  {(() => {
                    const isString = typeof content === "string";
                    const trimmed = isString ? (content as string).trim() : "";
                    const looksJson =
                      isString &&
                      (/^\{/.test(trimmed) ||
                        /"answer"\s*:|"justification"\s*:|"excerpts_and_references"/i.test(
                          trimmed
                        ));
                    const shouldShowPrelimText =
                      isString && !!trimmed && !looksJson;
                    // Once structured fields begin streaming, suppress the plain text
                    // to avoid duplicate rendering (unformatted + formatted).
                    return shouldShowPrelimText && !answer && !justification ? (
                      <div className={styles.messagePreliminaryText}>{content}</div>
                    ) : null;
                  })()}

                  {/* Answer section */}
                  {answer !== undefined && (
                    <div className={styles.messageAnswerSection}>
                      <div className={styles.messageAnswerLabel}>Answer</div>
                      <div className={styles.messageAnswerContent}>
                        {answer ? (
                          <ChatMarkdown>{answer}</ChatMarkdown>
                        ) : (
                          "No answer available"
                        )}
                      </div>
                    </div>
                  )}

                  {/* Justification section */}
                  {justification !== undefined && (
                    <div className={styles.messageJustificationSection}>
                      <div className={styles.messageJustificationLabel}>
                        Justification
                      </div>
                      <div className={styles.messageJustificationContent}>
                        {justification ? (
                          <ChatMarkdown>{justification}</ChatMarkdown>
                        ) : (
                          "No justification available"
                        )}
                      </div>
                    </div>
                  )}

                  {/* References section */}
                  {references && references.length > 0 && (
                    <div className={styles.messageReferencesContainer}>
                      {/* References header - clickable to toggle dropdown */}
                      <div
                        className={styles.messageReferencesHeader}
                        onClick={toggleRefs}
                      >
                        <div className={styles.messageReferencesHeaderContent}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="14"
                            viewBox="0 0 10 14"
                            fill="none"
                          >
                            <path
                              d="M8.88889 0H1.11111C0.816426 0 0.533811 0.118002 0.325437 0.328047C0.117063 0.538092 0 0.822974 0 1.12002V13.4403C4.93315e-05 13.5402 0.0266303 13.6383 0.0769846 13.7244C0.127339 13.8105 0.199632 13.8814 0.286361 13.9299C0.37309 13.9783 0.471096 14.0024 0.570207 13.9998C0.669319 13.9972 0.765924 13.9678 0.85 13.9149L5 11.3003L9.15069 13.9149C9.23476 13.9677 9.33129 13.9968 9.4303 13.9994C9.52931 14.0019 9.62719 13.9777 9.71381 13.9293C9.80043 13.8809 9.87264 13.8101 9.92295 13.7241C9.97327 13.6381 9.99987 13.5401 10 13.4403V1.12002C10 0.822974 9.88294 0.538092 9.67456 0.328047C9.46619 0.118002 9.18357 0 8.88889 0ZM8.88889 1.12002V9.07008L5.29375 6.80554C5.20545 6.74991 5.10343 6.72041 4.99931 6.72041C4.89518 6.72041 4.79316 6.74991 4.70486 6.80554L1.11111 9.06938V1.12002H8.88889ZM5.29375 10.1656C5.20545 10.11 5.10343 10.0805 4.99931 10.0805C4.89518 10.0805 4.79316 10.11 4.70486 10.1656L1.11111 12.4301V10.3903L5 7.94026L8.88889 10.3903V12.4301L5.29375 10.1656Z"
                              fill="currentColor"
                            />
                          </svg>
                          {references.length} relevant {references.length === 1 ? "reference" : "references"}
                        </div>

                        {/* Dropdown arrow icon that rotates when open */}
                        <div
                          className={`${styles.messageReferencesDropdownArrow} ${refsOpen
                            ? styles.messageReferencesDropdownArrowOpen
                            : styles.messageReferencesDropdownArrowClosed
                            }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="7"
                            height="12"
                            viewBox="0 0 7 12"
                            fill="none"
                          >
                            <path
                              d="M6.82907 11.0684C6.88326 11.1191 6.92625 11.1792 6.95558 11.2454C6.9849 11.3116 7 11.3826 7 11.4543C7 11.5259 6.9849 11.5969 6.95558 11.6631C6.92625 11.7293 6.88326 11.7895 6.82907 11.8402C6.77488 11.8908 6.71054 11.931 6.63974 11.9585C6.56893 11.9859 6.49305 12 6.41641 12C6.33977 12 6.26388 11.9859 6.19308 11.9585C6.12227 11.931 6.05794 11.8908 6.00375 11.8402L0.171065 6.38589C0.116834 6.33523 0.0738134 6.27508 0.0444609 6.20887C0.0151083 6.14265 0 6.07168 0 6C0 5.92832 0.0151083 5.85735 0.0444609 5.79113C0.0738134 5.72492 0.116834 5.66476 0.171065 5.61411L6.00375 0.159841C6.11319 0.0574964 6.26163 -2.8531e-09 6.41641 0C6.57119 2.8531e-09 6.71962 0.0574964 6.82907 0.159841C6.93851 0.262185 7 0.400994 7 0.54573C7 0.690467 6.93851 0.829275 6.82907 0.93162L1.40832 6L6.82907 11.0684Z"
                              fill="currentColor"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* References content - shown when expanded */}
                      <div
                        className={`${styles.messageReferencesContent} ${refsOpen
                          ? styles.messageReferencesContentOpen
                          : styles.messageReferencesContentClosed
                          }`}
                      >
                        {references.map((ref, index) => (
                          <div key={index} className={styles.messageReferenceItem}>
                            {/* Cited Text section */}
                            <div className={styles.messageReferenceItemCitedText}>
                              <div
                                className={
                                  styles.messageReferenceItemCitedTextLabel
                                }
                              >
                                Cited text
                              </div>
                              <div style={{ whiteSpace: "pre-line" }}>"{formatReferenceExcerpt(ref.citedText)}"</div>
                            </div>

                            {/* Reference and Page Number section */}
                            <div className={styles.messageReferenceItemMeta}>
                              <div className={styles.messageReferenceSource}>
                                <div
                                  className={styles.messageReferenceSourceHeader}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    fill="currentColor"
                                    viewBox="0 0 256 256"
                                    className={styles.referenceSourceIcon}
                                  >
                                    <path d="M208,24H72A32,32,0,0,0,40,56V224a8,8,0,0,0,8,8H192a8,8,0,0,0,0-16H56a16,16,0,0,1,16-16H208a8,8,0,0,0,8-8V32A8,8,0,0,0,208,24ZM120,40h48v72L148.79,97.6a8,8,0,0,0-9.6,0L120,112Zm80,144H72a31.82,31.82,0,0,0-16,4.29V56A16,16,0,0,1,72,40h32v88a8,8,0,0,0,12.8,6.4L144,114l27.21,20.4A8,8,0,0,0,176,136a8,8,0,0,0,8-8V40h16Z"></path>
                                  </svg>
                                  <div
                                    className={styles.messageReferenceSourceLabel}
                                  >
                                    Source:
                                  </div>
                                </div>
                                <div className={styles.messageReferenceSourceText}>
                                  {ref.type === "article" &&
                                    articleReferenceUrl(ref.details) ? (
                                    <a
                                      href={articleReferenceUrl(ref.details)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      aria-label={`Open cited passage in ${ref.reference}`}
                                      style={{
                                        textDecoration: "underline",
                                        fontWeight: 600,
                                        color: "inherit",
                                      }}
                                    >
                                      {ref.reference}
                                    </a>
                                  ) : (
                                    <>{ref.reference}</>
                                  )}
                                </div>
                              </div>
                              {ref.type === "article" ? (
                                <div className={styles.messageReferenceArticleMeta}>
                                  {ref.details?.access_level === "abstract" && <span>Abstract only</span>}
                                  {ref.details?.access_level === "guideline" && <span>Clinical guideline</span>}
                                  {ref.details?.access_level === "pdf" && ref.details.page_number && <span>PDF · Page {ref.details.page_number}</span>}
                                  <div
                                    className={styles.messageReferenceArticleRow}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      fill="currentColor"
                                      viewBox="0 0 256 256"
                                      style={{ marginRight: 6 }}
                                    >
                                      <path d="M208,48H96a8,8,0,0,0-8,8V200a8,8,0,0,0,8,8H208a8,8,0,0,0,8-8V56A8,8,0,0,0,208,48Zm-8,144H104V64H200ZM80,56V200a8,8,0,0,1-16,0V56A8,8,0,0,1,80,56Z"></path>
                                    </svg>
                                    <span>
                                      {ref.details?.journal || ref.details?.publisher || "N/A"}
                                    </span>
                                  </div>
                                  <div
                                    className={styles.messageReferenceArticleRow}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      fill="currentColor"
                                      viewBox="0 0 256 256"
                                      style={{ marginRight: 6 }}
                                    >
                                      <path d="M128,128a44,44,0,1,0-44-44A44,44,0,0,0,128,128Zm0,16c-35.35,0-64,25.07-64,56a8,8,0,0,0,8,8H184a8,8,0,0,0,8-8C192,169.07,163.35,144,128,144Z"></path>
                                    </svg>
                                    <span>
                                      {((ref as any).details
                                        ?.first_author as string) ||
                                        (Array.isArray(
                                          (ref as any).details?.authors
                                        )
                                          ? (ref as any).details?.authors[0] ||
                                          "N/A"
                                          : ((ref as any).details
                                            ?.authors as string) ||
                                          "N/A")}
                                    </span>
                                  </div>
                                  {/* Link row removed per UI update: title now clickable; hide separate DOI row */}
                                  <div
                                    className={styles.messageReferenceArticleRow}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      fill="currentColor"
                                      viewBox="0 0 256 256"
                                      style={{ marginRight: 6 }}
                                    >
                                      <path d="M200,40H184V24a8,8,0,0,0-16,0V40H88V24a8,8,0,0,0-16,0V40H56A16,16,0,0,0,40,56V208a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V56A16,16,0,0,0,200,40Zm0,168H56V88H200Zm0-136H56V56H72V72a8,8,0,0,0,16,0V56h80V72a8,8,0,0,0,16,0V56h16Z"></path>
                                    </svg>
                                    <span>
                                      {(() => {
                                        // Show year only. Prefer date_of_publication, then date.
                                        const d =
                                          ((ref as any).details
                                            ?.date_of_publication as string) ||
                                          ((ref as any).details?.date as string) ||
                                          "";
                                        if (!d) return "N/A";
                                        const year = d.trim().slice(0, 4);
                                        return year || "N/A";
                                      })()}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className={styles.messageReferenceTextbookMeta}>
                                  {ref.details?.edition != null &&
                                    String(ref.details.edition).trim() !== "" && (
                                      <div className={styles.messageReferenceEdition}>
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="18"
                                          height="18"
                                          fill="currentColor"
                                          viewBox="0 0 256 256"
                                        >
                                          <path d="M208,24H72A32,32,0,0,0,40,56V224a8,8,0,0,0,8,8H192a8,8,0,0,0,0-16H56a16,16,0,0,1,16-16H208a8,8,0,0,0,8-8V32A8,8,0,0,0,208,24ZM200,184H72a31.82,31.82,0,0,0-16,4.29V56A16,16,0,0,1,72,40H200Z"></path>
                                        </svg>
                                        <div className={styles.messageReferenceEditionLabel}>
                                          Edition:
                                        </div>
                                        <div className={styles.messageReferenceEditionText}>
                                          {String(ref.details.edition)}
                                        </div>
                                      </div>
                                    )}
                                  <div className={styles.messageReferencePageNumber}>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      fill="currentColor"
                                      viewBox="0 0 256 256"
                                    >
                                      <path d="M184,32H72A16,16,0,0,0,56,48V224a8,8,0,0,0,12.24,6.78L128,193.43l59.77,37.35A8,8,0,0,0,200,224V48A16,16,0,0,0,184,32Zm0,177.57-51.77-32.35a8,8,0,0,0-8.48,0L72,209.57V48H184Z"></path>
                                    </svg>
                                    <div className={styles.messageReferencePageLabel}>
                                      Page:
                                    </div>
                                    <div className={styles.messageReferencePageText}>
                                      {ref.pageNumber || "N/A"}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fallback for non-structured content inside a structured message */}
                  {!answer &&
                    !justification &&
                    (!references || references.length === 0) && (
                      <div className={styles.messageFallbackContent}>
                        {typeof content === "string"
                          ? content
                          : JSON.stringify(content)}
                      </div>
                    )}
                </div>
              ) : (
                // Render regular message (user messages or non-structured AI messages)
                (processing_status === "error" ||
                  hasFallbackText ||
                  showsOnlyProcessingIndicator) && (
                  <div className={styles.messageFallbackContent}>
                    {(() => {
                      const textContent = typeof content === "string" ? content : JSON.stringify(content);
                      if (!isUser && processing_status === "error") {
                        return (
                          <div className={styles.errorIndicator}>
                            <span className={styles.errorIndicatorTitle}>
                              Generation was interrupted.
                            </span>
                            <span className={styles.errorIndicatorText}>
                              Your message was saved. {error_message || "Please try again."}
                            </span>
                          </div>
                        );
                      }
                      // Show a spinner for empty AI messages (still waiting for response)
                      if (
                        showsOnlyProcessingIndicator
                      ) {
                        return (
                          <div className={styles.thinkingIndicator}>
                            <div className={`${styles.statusSpinner} ${styles.statusSpinnerLarge}`} />
                            <span className={styles.thinkingText}>
                              {processing_status === "pending" || processing_status === "processing"
                                ? "Answering"
                                : "Thinking"}
                              <ThinkingDots />
                            </span>
                          </div>
                        );
                      }
                      return !isUser && typeof textContent === "string" ? (
                        <ChatMarkdown>{textContent}</ChatMarkdown>
                      ) : (
                        textContent
                      );
                    })()}
                  </div>
                )
              )}

              {canShowAnswerActions && (
                <div className={styles.messageActionsArea}>
                  <div className={styles.messageActions} aria-label="Answer actions">
                    <button
                      type="button"
                      className={styles.messageActionButton}
                      onClick={handleCopy}
                      title="Copy to clipboard"
                      aria-label="Copy answer"
                    >
                      {copyStatus === "copied" ? (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>Copied</span>
                        </>
                      ) : copyStatus === "failed" ? (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                          </svg>
                          <span>Copy failed</span>
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                          <span>Copy</span>
                        </>
                      )}
                    </button>

                    {message_id && onReactionChange && (
                      <>
                        <button
                          type="button"
                          className={`${styles.messageActionButton} ${
                            userReaction?.reaction === "like"
                              ? styles.messageActionButtonSelected
                              : ""
                          }`}
                          onClick={() => handleReactionClick("like")}
                          disabled={reactionPending}
                          aria-label={
                            userReaction?.reaction === "like"
                              ? "Remove like from answer"
                              : "Like answer"
                          }
                          aria-pressed={userReaction?.reaction === "like"}
                        >
                          <svg viewBox="0 0 24 24" fill={userReaction?.reaction === "like" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M7 10v12H3V10h4Zm0 10h10.3a2 2 0 0 0 1.9-1.4l2.2-7A2 2 0 0 0 19.5 9H15l.7-4.1A2.5 2.5 0 0 0 13.2 2L7 10Z" />
                          </svg>
                          <span className={styles.reactionLabel}>Like</span>
                        </button>
                        <button
                          type="button"
                          className={`${styles.messageActionButton} ${
                            userReaction?.reaction === "dislike"
                              ? styles.messageActionButtonSelectedNegative
                              : ""
                          }`}
                          onClick={() => handleReactionClick("dislike")}
                          disabled={reactionPending}
                          aria-label={
                            userReaction?.reaction === "dislike"
                              ? "Remove dislike from answer"
                              : "Dislike answer"
                          }
                          aria-pressed={userReaction?.reaction === "dislike"}
                        >
                          <svg viewBox="0 0 24 24" fill={userReaction?.reaction === "dislike" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M17 14V2h4v12h-4Zm0-10H6.7a2 2 0 0 0-1.9 1.4l-2.2 7A2 2 0 0 0 4.5 15H9l-.7 4.1A2.5 2.5 0 0 0 10.8 22l6.2-8Z" />
                          </svg>
                          <span className={styles.reactionLabel}>Dislike</span>
                        </button>
                        {reactionPending && (
                          <span className={styles.reactionSaving} role="status">
                            Saving…
                          </span>
                        )}
                      </>
                    )}
                    {message_id && <AuthenticatedSaveNote messageId={message_id} savedNoteId={savedNoteId} />}
                  </div>

                  {userReaction?.reaction === "dislike" && !reasonsOpen && (
                    <button
                      type="button"
                      className={styles.addFeedbackDetailsButton}
                      onClick={() => setReasonsOpen(true)}
                    >
                      {userReaction.reason_codes.length > 0
                        ? "Edit feedback details"
                        : "Tell us what went wrong"}
                    </button>
                  )}

                  {userReaction?.reaction === "dislike" && reasonsOpen && (
                    <div className={styles.dislikeReasonPanel}>
                      <div className={styles.dislikeReasonHeader}>
                        <div>
                          <strong>What went wrong?</strong>
                          <span>Optional — choose all that apply.</span>
                        </div>
                        <button
                          type="button"
                          className={styles.reasonDoneButton}
                          onClick={() => setReasonsOpen(false)}
                        >
                          Done
                        </button>
                      </div>
                      <div className={styles.dislikeReasonChips}>
                        {DISLIKE_REASONS.map((reason) => {
                          const selected = userReaction.reason_codes.includes(reason.code);
                          return (
                            <button
                              key={reason.code}
                              type="button"
                              className={`${styles.dislikeReasonChip} ${
                                selected ? styles.dislikeReasonChipSelected : ""
                              }`}
                              onClick={() => toggleReason(reason.code)}
                              disabled={reactionPending}
                              aria-pressed={selected}
                            >
                              {reason.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {reactionError && (
                    <div className={styles.reactionError} role="alert">
                      {reactionError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Investigation rows share the final answer's single timestamp. */}
        {!hidesAssistantTimestamp && (
          <div className={styles.messageTimestamp}>
            {formatTimestamp(timestamp)}
          </div>
        )}
      </div>
    );
  }
);

// ThreadContent component to display a full conversation thread
const ThreadContent: React.FC<ThreadContentProps> = ({
  threadId,
  threadMessages = [],
  isLoading = false,
  processingStatus = "",
  isTransitioning,
  scrollToBottomRequest = 0,
  reactionPendingMessageIds = new Set<string>(),
  reactionErrors = {},
  onReactionChange,
}) => {
  // Ref for the scrollable message container
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const userHasScrolledUpRef = useRef(false);
  const programmaticScrollRef = useRef(false);
  const programmaticScrollTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const prevMessageCount = useRef(threadMessages.length);
  // Track last known scrollHeight to detect incremental growth during streaming
  const prevScrollHeight = useRef<number>(0);

  const updateUserHasScrolledUp = useCallback((hasScrolledUp: boolean) => {
    userHasScrolledUpRef.current = hasScrolledUp;
    setUserHasScrolledUp((current) =>
      current === hasScrolledUp ? current : hasScrolledUp
    );
  }, []);

  const cancelProgrammaticScroll = useCallback(() => {
    if (programmaticScrollTimerRef.current) {
      clearTimeout(programmaticScrollTimerRef.current);
      programmaticScrollTimerRef.current = null;
    }
    programmaticScrollRef.current = false;
  }, []);

  const scrollToBottom = useCallback(
    (behavior: "auto" | "smooth") => {
      const container = scrollableContainerRef.current;
      if (!container) return;

      cancelProgrammaticScroll();
      programmaticScrollRef.current = true;
      container.scrollTo({ top: container.scrollHeight, behavior });

      // Ignore the scroll events generated by our own scroll. Smooth scrolling
      // remains active longer than an instant streaming adjustment.
      programmaticScrollTimerRef.current = setTimeout(
        () => {
          programmaticScrollRef.current = false;
          programmaticScrollTimerRef.current = null;
        },
        behavior === "smooth" ? 700 : 0
      );
    },
    [cancelProgrammaticScroll]
  );

  // A newly selected conversation starts in follow mode. The user can opt out
  // again by scrolling upward once its content is visible.
  useEffect(() => {
    updateUserHasScrolledUp(false);
  }, [threadId, updateUserHasScrolledUp]);

  // State for thread info only
  const [threadInfo, setThreadInfo] = useState<{
    title: string;
    created_at: string;
  } | null>(null);

  // Fade transition state
  const [fadeClass, setFadeClass] = useState<string>("fade-enter-active");

  // Get user from auth context
  const { user } = useAuthContext();
  const isInitialMessageLoad = isLoading && threadMessages.length === 0;

  // Effect to handle fade transition
  useEffect(() => {
    if (isTransitioning) {
      setFadeClass("fade-exit-active");
    } else {
      setFadeClass("fade-enter-active");
    }
  }, [isTransitioning, threadId]);

  // Effect to handle all scrolling logic
  useEffect(() => {
    const container = scrollableContainerRef.current;
    if (!container) return;

    const isNewMessage = threadMessages.length > prevMessageCount.current;
    const isInitialLoad =
      prevMessageCount.current === 0 && threadMessages.length > 0;

    // Detect streaming growth: content height increased but message count may not have
    const hasGrown = container.scrollHeight > (prevScrollHeight.current || 0);

    // Scroll instantly on initial load of messages
    if (isInitialLoad) {
      updateUserHasScrolledUp(false);
      scrollToBottom("auto");
    }
    // Scroll smoothly for new messages unless the user has scrolled up
    else if (isNewMessage && !userHasScrolledUpRef.current) {
      scrollToBottom("smooth");
    }
    // Keep the view pinned while content streams. Use auto here so completion
    // does not queue a long top-to-bottom smooth scroll across a tall answer.
    else if (hasGrown && !userHasScrolledUpRef.current) {
      scrollToBottom("auto");
    }

    prevMessageCount.current = threadMessages.length;
    prevScrollHeight.current = container.scrollHeight;
  }, [
    threadMessages,
    userHasScrolledUp,
    processingStatus,
    threadId,
    scrollToBottom,
    updateUserHasScrolledUp,
  ]);

  // Force smooth scroll to bottom when requested (e.g., immediately after user sends)
  useEffect(() => {
    if (scrollToBottomRequest > 0) {
      updateUserHasScrolledUp(false);
      scrollToBottom("smooth");
    }
  }, [scrollToBottomRequest, scrollToBottom, updateUserHasScrolledUp]);

  // Effect to detect user scroll and disable/re-enable auto-scrolling
  useEffect(() => {
    const container = scrollableContainerRef.current;
    if (!container) return;

    const isAtBottom = () =>
      container.scrollHeight - container.clientHeight - container.scrollTop <=
      24;

    const disengageAutoScroll = () => {
      cancelProgrammaticScroll();
      // Explicitly stop a smooth scroll that may still be in flight.
      container.scrollTo({ top: container.scrollTop, behavior: "auto" });
      updateUserHasScrolledUp(true);
    };

    const handleScroll = () => {
      if (programmaticScrollRef.current) return;
      updateUserHasScrolledUp(!isAtBottom());
    };

    const handleWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) {
        disengageAutoScroll();
      }
    };

    let previousTouchY: number | null = null;
    const handleTouchStart = (event: TouchEvent) => {
      previousTouchY = event.touches[0]?.clientY ?? null;
    };
    const handleTouchMove = (event: TouchEvent) => {
      const touchY = event.touches[0]?.clientY;
      if (
        touchY !== undefined &&
        previousTouchY !== null &&
        touchY > previousTouchY + 2
      ) {
        disengageAutoScroll();
      }
      previousTouchY = touchY ?? null;
    };
    const handleTouchEnd = () => {
      previousTouchY = null;
    };

    const handlePointerDown = (event: PointerEvent) => {
      const { right } = container.getBoundingClientRect();
      const isUsingScrollbar = event.clientX >= right - 18;
      if (isUsingScrollbar) {
        disengageAutoScroll();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.matches("input, textarea, [contenteditable='true']")
      ) {
        return;
      }

      if (
        event.key === "ArrowUp" ||
        event.key === "PageUp" ||
        event.key === "Home" ||
        (event.key === " " && event.shiftKey)
      ) {
        disengageAutoScroll();
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    container.addEventListener("wheel", handleWheel, { passive: true });
    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: true,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });
    container.addEventListener("pointerdown", handlePointerDown, {
      passive: true,
    });
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
      cancelProgrammaticScroll();
    };
  }, [cancelProgrammaticScroll, updateUserHasScrolledUp]);

  // Group consecutive tool-call bubbles into a single grouped bubble for cleaner UI
  const groupedItems = useMemo(() => {
    type ThreadMsg = NonNullable<ThreadContentProps["threadMessages"]>[number];
    type ThreadMsgList = NonNullable<ThreadContentProps["threadMessages"]>;
    type RenderItem =
      | { kind: "single"; msg: ThreadMsg }
      | { kind: "tool_group"; items: ThreadMsgList; key: string };

    const items: RenderItem[] = [];
    let buffer: ThreadMsgList = [];

    const flushBuffer = () => {
      if (buffer.length === 0) return;
      const key = `tool-group-${buffer[0]?.message_id || Math.random()}`;
      items.push({ kind: "tool_group", items: buffer.slice(), key });
      buffer = [];
    };

    const msgs = (threadMessages as ThreadMsgList) || [];
    for (const m of msgs) {
      if (m?.isToolCall) {
        buffer.push(m);
      } else {
        flushBuffer();
        items.push({ kind: "single", msg: m });
      }
    }
    flushBuffer();

    return items as RenderItem[];
  }, [threadMessages]);

  const firstCommentaryIdByGeneration = useMemo(() => {
    const firstIds = new Map<string, string>();
    for (const message of threadMessages) {
      if (
        message.isAgentCommentary &&
        message.generationId &&
        !firstIds.has(message.generationId)
      ) {
        firstIds.set(message.generationId, message.message_id);
      }
    }
    return firstIds;
  }, [threadMessages]);

  // Track expand/collapse state for each tool group using its stable key
  const [toolGroupOpenMap, setToolGroupOpenMap] = useState<
    Record<string, boolean>
  >({});

  // Initialize new groups to open while processing; collapse all when processing completes
  useEffect(() => {
    // Build the next map: keep existing toggles; add defaults for new keys
    const next: Record<string, boolean> = { ...toolGroupOpenMap };
    for (const item of groupedItems) {
      if ((item as any).kind === "tool_group") {
        const key = (item as any).key as string;
        if (!(key in next)) {
          // Default behavior: show steps while the agent is processing
          next[key] = !!processingStatus;
        }
      }
    }
    // If processing just finished (empty status), collapse all groups
    if (!processingStatus && Object.keys(next).length) {
      for (const k of Object.keys(next)) next[k] = false;
    }
    // Update state only if something actually changed
    const changed =
      Object.keys(next).length !== Object.keys(toolGroupOpenMap).length ||
      Object.keys(next).some((k) => toolGroupOpenMap[k] !== next[k]);
    if (changed) setToolGroupOpenMap(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedItems, processingStatus]);

  // Toggle helper for a specific group key
  const toggleToolGroup = (key: string) => {
    setToolGroupOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Effect to fetch thread info when threadId changes
  useEffect(() => {
    let isActive = true;
    const applyThreadInfo = (
      info: { title: string; created_at: string } | null
    ) => {
      if (isActive) {
        setThreadInfo(info);
      }
    };

    applyThreadInfo(null);

    if (threadId && user?.id) {
      const fetchThreadInfo = async () => {
        try {
          const response = await authenticatedFetch(
            `${process.env.REACT_APP_API_URL}/api/threads/${user.id}`,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) {
            throw new Error(
              `Error fetching thread info: ${response.statusText}`
            );
          }

          const threadsData = await response.json();
          const currentThread = threadsData.find(
            (thread: any) => thread.thread_id === threadId
          );

          if (currentThread) {
            applyThreadInfo({
              title: currentThread.title || "New Thread",
              created_at: currentThread.created_at,
            });
          } else {
            applyThreadInfo({
              title: "Thread",
              created_at: new Date().toISOString(),
            });
          }
        } catch (err) {
          console.error("Error fetching thread info:", err);
          applyThreadInfo({
            title: "Thread",
            created_at: new Date().toISOString(),
          });
        }
      };

      fetchThreadInfo();
    } else if (threadId && !user?.id) {
      // Guest: derive thread info (title, created_at) from messages
      const fetchGuestThreadInfo = async () => {
        try {
          const response = await authenticatedFetch(
            `${process.env.REACT_APP_API_URL}/api/messages/${threadId}`,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) {
            throw new Error(
              `Error fetching messages for guest thread: ${response.statusText}`
            );
          }

          const msgs = await response.json();
          const createdAt = msgs?.[0]?.created_at || new Date().toISOString();
          const firstUser = (msgs || []).find(
            (m: any) => m.role === "user" && m.type === "regular"
          );
          let title = "New Thread";
          if (firstUser) {
            const raw =
              typeof firstUser.content === "string"
                ? firstUser.content
                : JSON.stringify(firstUser.content);
            title = raw.substring(0, 50) + (raw.length > 50 ? "..." : "");
          }
          applyThreadInfo({ title, created_at: createdAt });
        } catch (err) {
          console.error("Error deriving guest thread info:", err);
          applyThreadInfo({
            title: "New Thread",
            created_at: new Date().toISOString(),
          });
        }
      };

      fetchGuestThreadInfo();
    }

    return () => {
      isActive = false;
    };
  }, [threadId, user?.id]);

  const hasInlineActivityProgress = threadMessages.some(
    (message) =>
      message.sender === "ai" &&
      !message.isToolCall &&
      (message.processing_status === "pending" ||
        message.processing_status === "processing") &&
      Array.isArray(message.progressSteps) &&
      message.progressSteps.length > 0
  );

  return (
    <div
      className={`${styles.threadContent} ${fadeClass === "fade-enter-active"
        ? styles.fadeEnterActive
        : styles.fadeExitActive
        }`}
    >
      {/* Header section */}
      {processingStatus && !hasInlineActivityProgress && (
        <div className={styles.threadHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.statusLoader}>
              <div className={styles.statusSpinner} />
              <span>
                {processingStatus.replace(/\.{2,}$/, '')}
                <ThinkingDots />
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Messages container */}
      <div
        ref={scrollableContainerRef}
        className={styles.scrollableContentWrapper}
      >
        <div className={styles.threadBody}>
          {/* Thread title */}
          <div className={styles.threadTitle}>
            {threadInfo ? threadInfo.title : "Loading..."}
          </div>

          {/* Loading indicator */}
          {isInitialMessageLoad ? (
            <div className={styles.loadingContainer}>
              Loading thread messages...
            </div>
          ) : threadMessages.length === 0 ? (
            <div className={styles.emptyContainer}>
              No messages in this thread yet. Send a message to start the
              conversation.
            </div>
          ) : (
            // Display messages with grouping for consecutive tool-call bubbles
            groupedItems.map((item, index) => {
              if (item.kind === "single") {
                const message = item.msg;
                const previousItem = groupedItems[index - 1];
                const attachedResearchGroup =
                  previousItem?.kind === "tool_group" &&
                    message.sender === "ai" &&
                    !message.isToolCall &&
                    !message.isAgentCommentary
                    ? previousItem
                    : undefined;
                const researchGroupKey = attachedResearchGroup?.key;
                const researchIsActive =
                  !!processingStatus &&
                  (message.processing_status === "pending" ||
                    message.processing_status === "processing" ||
                    index === groupedItems.length - 1);
                const commentaryGenerationId = message.isAgentCommentary
                  ? message.generationId
                  : message.message_id;
                const turnIdentityMessageId = commentaryGenerationId
                  ? firstCommentaryIdByGeneration.get(
                    commentaryGenerationId
                  )
                  : undefined;
                const showAssistantLogo = turnIdentityMessageId
                  ? message.message_id === turnIdentityMessageId
                  : undefined;

                return (
                  <Message
                    key={message.message_id || `message-${index}`}
                    message_id={message.message_id}
                    content={message.content}
                    sender={message.sender}
                    timestamp={message.timestamp}
                    isToolCall={message.isToolCall}
                    isAgentCommentary={message.isAgentCommentary}
                    commentaryId={message.commentaryId}
                    isCommentaryStreaming={message.isCommentaryStreaming}
                    generationId={message.generationId}
                    showAssistantLogo={showAssistantLogo}
                    processing_status={message.processing_status}
                    error_message={message.error_message}
                    progressSteps={message.progressSteps}
                    answer={message.answer}
                    justification={message.justification}
                    references={message.references}
                    savedNoteId={message.savedNoteId}
                    userReaction={message.userReaction}
                    reactionPending={reactionPendingMessageIds.has(message.message_id)}
                    reactionError={reactionErrors[message.message_id]}
                    onReactionChange={onReactionChange}
                    researchItems={attachedResearchGroup?.items as MessageProps[] | undefined}
                    researchIsActive={researchIsActive}
                    researchIsOpen={researchGroupKey ? !!toolGroupOpenMap[researchGroupKey] : false}
                    onResearchToggle={
                      researchGroupKey
                        ? () => toggleToolGroup(researchGroupKey)
                        : undefined
                    }
                  />
                );
              }

              // Group of consecutive tool-call bubbles with a branded collapsible header
              const groupKey = item.key || `tool-group-${index}`;
              const nextItem = groupedItems[index + 1];
              if (
                nextItem?.kind === "single" &&
                nextItem.msg.sender === "ai" &&
                !nextItem.msg.isToolCall &&
                !nextItem.msg.isAgentCommentary
              ) {
                return null;
              }

              const isOpen = !!toolGroupOpenMap[groupKey];
              return (
                <div
                  key={groupKey}
                  className={`${styles.messageContainer} ${styles.messageContainerAi}`}
                  data-message-kind="research"
                >
                  <ResearchTimeline
                    items={item.items as MessageProps[]}
                    isActive={!!processingStatus}
                    isOpen={isOpen}
                    onToggle={() => toggleToolGroup(groupKey)}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ThreadContent;
